import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, api } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { exponentOf } from '@/lib/currencies';
import { formatMinor, minorToPlain, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useOpenBills, type PurchaseDocument } from '@/lib/purchases';

/** E6 "Apply to bills": settle the vendor's open bills from a debit note (original bill first). */
export function ApplyDebitNoteDialog({
  note,
  open,
  onClose,
}: {
  note: PurchaseDocument;
  open: boolean;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const bills = useOpenBills(open ? note.contactId : null);
  const [date, setDate] = useState(todayIso());
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  // amounts are in the document's currency (multi-currency, migration 0900)
  const ccy = note.currency;
  const exp = exponentOf(ccy);
  const available = toMinor(note.balance, exp) ?? 0n;

  useEffect(() => {
    if (!open || !bills.data) return;
    setDate(todayIso() < note.issueDate ? note.issueDate : todayIso());
    const next: Record<string, string> = {};
    let left = available;
    const list = [...bills.data.bills].sort((a, b) =>
      a.id === note.relatedDocumentId ? -1 : b.id === note.relatedDocumentId ? 1 : 0,
    );
    for (const b of list) {
      if (left <= 0n) break;
      const bal = toMinor(b.balance, exp) ?? 0n;
      const take = bal < left ? bal : left;
      next[b.id] = minorToPlain(take, exp);
      left -= take;
    }
    setAmounts(next);
  }, [open, bills.data, available, note.issueDate, note.relatedDocumentId]);

  const total = Object.values(amounts).reduce((s, v) => s + (toMinor(v, exp) ?? 0n), 0n);
  const m = useMutation({
    mutationFn: () =>
      api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents/${note.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({
          date,
          allocations: Object.entries(amounts)
            .filter(([, v]) => (toMinor(v, exp) ?? 0n) > 0n)
            .map(([documentId, amount]) => ({ documentId, amount })),
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      toast(`Debit note ${note.number}: ${ccy} ${formatMinor(total, exp)} applied.`);
      onClose();
    },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Apply debit note {note.number}</DialogTitle>
          <DialogDescription>
            {ccy} {formatAmount(note.balance)} of credit from {note.contactName}. Applying posts
            nothing new: the debit note already reduced accounts payable.
          </DialogDescription>
        </DialogHeader>
        {m.error ? (
          <ErrorBanner>
            {m.error instanceof ApiError ? m.error.message : String(m.error)}
          </ErrorBanner>
        ) : null}
        <Field label="Date applied" className="w-[200px]">
          {(id) => <DateInput id={id} value={date} onChange={setDate} />}
        </Field>
        {bills.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : bills.data?.bills.length ? (
          <table className="w-full text-sm" aria-label="Open bills">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr className="border-b">
                <th className="py-2">Bill</th>
                <th className="py-2">Due date</th>
                <th className="py-2 text-right">Open balance</th>
                <th className="w-[160px] py-2 text-right">Apply</th>
              </tr>
            </thead>
            <tbody>
              {bills.data.bills.map((b) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="py-1.5">
                    {b.number}
                    {b.reference ? (
                      <span className="text-muted-foreground"> · {b.reference}</span>
                    ) : null}
                  </td>
                  <td className="py-1.5">{b.dueDate ? formatDate(b.dueDate) : ''}</td>
                  <td className="tnum py-1.5 text-right">{formatAmount(b.balance)}</td>
                  <td className="py-1">
                    <AmountInput
                      exp={exp}
                      aria-label={`Apply to ${b.number}`}
                      value={amounts[b.id] ?? ''}
                      onValueChange={(v) => setAmounts((a) => ({ ...a, [b.id]: v }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">This vendor has no open bills.</p>
        )}
        <p
          className={`text-sm ${total > available ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          Applying {ccy} {formatMinor(total, exp)} of {ccy} {formatMinor(available, exp)}.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={m.isPending || total === 0n || total > available}
            onClick={() => m.mutate()}
          >
            {m.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
